import { useEffect, useState } from "react";
import { fileUrl } from "@/lib/api";
import { getBlobUrl, getBlobUrlSync } from "@/lib/imageBlobCache";

/**
 * Renders an <img> that resolves to a persistent blob: URL from the
 * IndexedDB image cache when available, falling back to the direct
 * `/api/files/{path}` URL while the blob is being fetched. This means
 * a returning kiosk paints the pixels from RAM without a network
 * round-trip.
 *
 * `src` prop is treated as the FALLBACK — used when `path` is null,
 * or as an initial placeholder before path resolution. We destructure
 * it out of `rest` so it can never override the resolved blob URL
 * when spread onto the underlying <img>.
 *
 * Critical: when a category is folded/unfolded on the iPad menu the
 * <img> is unmounted and remounted. Initialising `src` with the
 * SYNCHRONOUS `getBlobUrlSync()` result means the remount paints
 * straight from the in-memory `URL.createObjectURL` binding rather
 * than briefly falling back to the direct `/api/files/…` URL — which
 * the ingress serves with `Cache-Control: no-store`, forcing a fresh
 * network fetch every time the tile came back into view.
 *
 * Extra props are forwarded to the underlying <img>.
 */
export default function BlobImage({ path, src: fallbackSrc, ...rest }) {
  const initial = () => {
    if (!path) return fallbackSrc || "";
    return getBlobUrlSync(path) || fileUrl(path);
  };
  const [src, setSrc] = useState(initial);

  useEffect(() => {
    let live = true;
    if (!path) { setSrc(fallbackSrc || ""); return; }
    const cached = getBlobUrlSync(path);
    if (cached) {
      setSrc(cached);
    } else {
      setSrc(fileUrl(path));
      getBlobUrl(path).then((url) => {
        if (live && url) setSrc(url);
      });
    }
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  return <img src={src} {...rest} />;
}
